import { Button } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { showToast, SwipableDialog } from '@tix-ticketing/ui-components';
import { HoldIcon } from '@tix-ticketing/ui-icons/feature';
import { AddCircleIcon } from '@tix-ticketing/ui-icons/outline';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { OrderResponse } from 'api/generated';
import { postApiOrdersByEncryptedOrderIdExpiryExtend } from 'api/generated';
import { useAppState } from 'lib/app-state';
import { minutesUntil } from 'lib/helpers/date-helpers';
import { toastError } from 'lib/helpers/error-toast-helpers';
import { handleOrderNotFound } from 'lib/helpers/order-helpers';
export const ExtendTimerDialog = ({
  encryptedOrderId,
  open,
  onClose,
  onExtended
}: {
  encryptedOrderId: string;
  open: boolean;
  onClose: () => void;
  onExtended?: () => void;
}) => {
  const intl = useIntl();
  const appState = useAppState();
  useEffect(() => {
    if (!appState.eventData.order) onClose();
  }, [appState.eventData.order, onClose]);

  // Data handling
  const extendTimerMutation = useMutation({
    mutationFn: () => postApiOrdersByEncryptedOrderIdExpiryExtend({
      path: {
        encryptedOrderId: encryptedOrderId
      }
    }),
    onSuccess: (data: any) => {
      handleOrderNotFound(data, () => {
        appState.setEventData(prev => ({
          ...prev,
          order: undefined
        }));
        onClose();
      });
      if (data.status !== 200) {
        toastError(data.response.data);
      } else {
        const extendedOrder = data.data as OrderResponse;
        showToast({
          title: intl.formatMessage({
            id: "common.success",
            defaultMessage: [{
              "type": 0,
              "value": "Success"
            }]
          }),
          message: intl.formatMessage({
            id: "cart.extendTimeDialog.extended",
            defaultMessage: [{
              "type": 0,
              "value": "Your time has been extended to "
            }, {
              "type": 6,
              "value": "minutes",
              "options": {
                "one": {
                  "value": [{
                    "type": 7
                  }, {
                    "type": 0,
                    "value": " minute"
                  }]
                },
                "other": {
                  "value": [{
                    "type": 7
                  }, {
                    "type": 0,
                    "value": " minutes"
                  }]
                }
              },
              "offset": 0,
              "pluralType": "cardinal"
            }, {
              "type": 0,
              "value": "."
            }]
          }, {
            minutes: minutesUntil(extendedOrder.expires)
          }),
          severity: 'success'
        });
        appState.setEventData({
          event: appState.eventData.event,
          order: extendedOrder
        });
        onExtended?.();
        onClose();
      }
    }
  });
  return <SwipableDialog open={open} onClose={onClose} maxWidth="sm" headerIcon={<HoldIcon />} headerText={intl.formatMessage({
    id: "cart.extendTimeDialog.title",
    defaultMessage: [{
      "type": 0,
      "value": "Your order is about to expire"
    }]
  })} subtitleText={intl.formatMessage({
    id: "cart.extendTimeDialog.description",
    defaultMessage: [{
      "type": 0,
      "value": "You have "
    }, {
      "type": 6,
      "value": "minutes",
      "options": {
        "one": {
          "value": [{
            "type": 7
          }, {
            "type": 0,
            "value": " minute"
          }]
        },
        "other": {
          "value": [{
            "type": 7
          }, {
            "type": 0,
            "value": " minutes"
          }]
        }
      },
      "offset": 0,
      "pluralType": "cardinal"
    }, {
      "type": 0,
      "value": " left to complete your purchase. To avoid losing your tickets, please complete your purchase soon."
    }]
  }, {
    minutes: minutesUntil(appState.eventData.order?.expires)
  })} primaryActionButton={<Button variant="contained" color="primary" size="large" endIcon={<AddCircleIcon />} onClick={() => extendTimerMutation.mutate()} loading={extendTimerMutation.isPending}>
          <FormattedMessage id="cart.extendTime" defaultMessage={[{
      "type": 0,
      "value": "Extend time"
    }]} />
        </Button>} />;
};